import * as L from 'leaflet';
import { createPinDivIcon } from './pin-shape';

/**
 * The app's single Leaflet basemap — CARTO Positron ("light_all"), matching
 * the Monitor page's fleet map. Use this tile layer (instantiate one per map
 * instance — Leaflet layers aren't shareable across maps) wherever the app
 * shows a Leaflet map, so every map in the product looks the same.
 *
 * CARTO now requires an API key on tile requests (free up to a fair-use
 * limit of 5M requests/month — carto.com/basemaps/apikey). Pass
 * `EnvConfig.cartoApiKey` (each caller injects `ENV_CONFIG` itself — this
 * is a plain function, not a service). Without a key, tiles will fail to
 * load (grey panel) rather than the map silently degrading gracefully —
 * unlike `WeatherService`, there's no "just hide the widget" fallback for
 * a map.
 *
 * Attribution must keep both "OpenStreetMap" and "CARTO" — see CARTO's
 * attribution requirements. Confirm CARTO's terms for production volume,
 * or swap this one file if the estate licenses a different basemap.
 */
export function createBasemapLayer(apiKey?: string): L.TileLayer {
  const keyParam = apiKey ? `?key=${encodeURIComponent(apiKey)}` : '';
  return L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png${keyParam}`, {
    attribution: '© OpenStreetMap © CARTO',
    maxZoom: 19,
  });
}

/**
 * The app's single-location pin — the same teardrop shape as the Monitor
 * page's fleet-status pins (`shared/utils/pin-shape.ts`), plain accent-blue
 * fill and no status glyph, since a "this one place" marker (add/edit
 * device, device overview) has no status to carry. One shape app-wide, not
 * a second marker style for maps that only ever show a single point.
 */
export function createLocationMarker(latlng: L.LatLngExpression): L.Marker {
  return L.marker(latlng, {
    icon: createPinDivIcon({ width: 30, height: 40, fill: '#007FE9' }),
  });
}
